import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([{ _id: "line-0", item_id: "", quantity: 1 }]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [s, i] = await Promise.all([api.get("/sales"), api.get("/inventory")]);
    setSales(s.data);
    setItems(i.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const uid = () => (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));

  const addLine = () => setLines([...lines, { _id: uid(), item_id: "", quantity: 1 }]);
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updLine = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const submit = async (e) => {
    e.preventDefault();
    const clean = lines.filter((l) => l.item_id && Number(l.quantity) > 0)
      .map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity) }));
    if (clean.length === 0) {
      toast.error("Add at least one line");
      return;
    }
    try {
      await api.post("/sales", { items: clean, note: note || null });
      toast.success("Sale recorded, stock deducted");
      setShowForm(false);
      setLines([{ _id: "line-0", item_id: "", quantity: 1 }]);
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const itemName = (id) => items.find((i) => i.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="sales-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-500 font-mono-plex mb-2">
            Consumption
          </div>
          <h1 className="font-display text-4xl font-bold">Sales</h1>
          <p className="text-slate-500 text-sm mt-1">
            Log sales to auto-deduct inventory.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-amber rounded-md px-4 py-2 text-sm flex items-center gap-2" data-testid="add-sale-btn">
          <Plus className="w-4 h-4" /> Log sale
        </button>
      </div>

      <div className="surface-card rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-slate-500 text-sm">Loading…</div>
        ) : sales.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <div className="font-display text-2xl mb-2">No sales recorded</div>
            <p className="text-sm">Log your first sale to see it here.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-slate-500 text-xs uppercase tracking-widest">
                <th className="text-left px-6 py-3">When</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-right px-6 py-3">Cost basis</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`row-sale-${s.id}`}>
                  <td className="px-6 py-3 text-slate-400 font-mono-plex text-xs">
                    {new Date(s.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    {s.items.map((it, idx) => (
                      <div key={`${it.item_id}-${idx}`} className="text-sm">
                        {it.item_name} <span className="text-slate-500 font-mono-plex">× {it.quantity}</span>
                      </div>
                    ))}
                    {s.note && <div className="text-xs text-slate-500 mt-1 italic">{s.note}</div>}
                  </td>
                  <td className="px-6 py-3 text-right font-mono-plex text-amber-500">
                    ${Number(s.total_cost).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <form
            onSubmit={submit}
            className="w-full max-w-lg bg-[#12141a] border border-white/10 rounded-xl p-6 space-y-4"
            data-testid="sale-form"
          >
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl font-bold">Log sale</div>
              <button type="button" onClick={() => setShowForm(false)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l._id} className="flex gap-2 items-center">
                  <select
                    value={l.item_id}
                    onChange={(e) => updLine(i, "item_id", e.target.value)}
                    className="flex-1 bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-amber-500"
                    data-testid={`sale-line-item-${i}`}
                  >
                    <option value="">— select item —</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name} (stock {it.current_stock})
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={l.quantity}
                    onChange={(e) => updLine(i, "quantity", e.target.value)}
                    className="w-24 bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-amber-500"
                    data-testid={`sale-line-qty-${i}`}
                  />
                  <button type="button" onClick={() => rmLine(i)} className="p-2 hover:bg-red-500/10 rounded" disabled={lines.length === 1}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addLine} className="text-sm text-amber-500 hover:text-amber-400" data-testid="add-line">
                + Add line
              </button>
            </div>

            <div>
              <label className="text-xs text-slate-400 mb-1 block">Note (optional)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-amber-500"
                data-testid="sale-note"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm hover:bg-white/[0.03]">
                Cancel
              </button>
              <button type="submit" className="btn-amber rounded-md px-4 py-2 text-sm" data-testid="save-sale">
                Log sale
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
